import { useEffect, useState } from 'preact/hooks';
import { NPCS } from '../../content/npcs';
import { CARDS } from '../../game/build';
import type { Game } from '../../game/Game';
import { BOND_STAGES, BOND_STAGE_NAMES } from '../../game/Progression';
import { Window } from '../../ui/app/Window';
import { bump, ui } from '../../ui/app/store';
import { Bar, ItemIcon, Silver, cssHue, itemName } from '../haven/widgets';
import { EMOTES, EMOTES_PER_DAY, GIFTS_PER_DAY, type GiftPref, SONGS, SONGS_PER_DAY, STAGE_COLORS, bondNpcs, profileFor } from './data';
import { EmoteGlyph } from './EmotesPanel';
import { performPlayerEmote, performSong } from './emotes';
import { type BondResult, MAX_STAGE, dailyUsed, emoteFor, giftPoints, giftPreference, giveGift, isGiftable, playSong, songsHeardToday, stageProgress, stageRewards } from './logic';
import { bondsGame, npcPresence } from './runtime';

/** Bonds window (T or the Bonds service): NPC cards, stage track, songs/emotes/gifts and stage rewards. */

type Mode = 'song' | 'emote' | 'gift';
interface Reaction {
  npc: string;
  text: string;
  points: number;
  tone: 'great' | 'good' | 'poor';
  stageUp: number | null;
}

const PREF_MARK: Record<GiftPref, string> = { loved: '♥', liked: '♡', neutral: '', disliked: '✕' };

/** Crest letter: the given name (the last word, so "High Warden Aldara" → A). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[parts.length - 1]?.[0] ?? '?').toUpperCase();
}

function Crest({ npc, stage, size = 46 }: { npc: string; stage: number; size?: number }) {
  const prof = profileFor(npc);
  const hue = cssHue((prof.card ? CARDS[prof.card]?.hue : undefined) ?? 0x8a8a8a);
  const name = NPCS[npc]?.name ?? npc;
  return (
    <span class="bd-crest" style={{ width: size, height: size, '--stage': STAGE_COLORS[stage] ?? '#999', '--hue': hue }}>
      <svg viewBox="0 0 48 48">
        <path d="M24 2 L44 13 L44 35 L24 46 L4 35 L4 13 Z" class="bd-crest-bg" />
        <path d="M24 6 L40.5 15.2 L40.5 32.8 L24 42 L7.5 32.8 L7.5 15.2 Z" class="bd-crest-in" />
      </svg>
      <b style={{ fontSize: Math.round(size * 0.36) }}>{initials(name)}</b>
    </span>
  );
}

function NpcCard({ game, npc, sel, onSel }: { game: Game; npc: string; sel: boolean; onSel: () => void }) {
  const prog = game.progression;
  const def = NPCS[npc];
  if (!prog || !def) return null;
  const sp = stageProgress(prog.char.bonds[npc] ?? 0);
  const here = npcPresence(game, npc);
  return (
    <button class={`bd-card${sel ? ' sel' : ''}`} style={{ '--stage': STAGE_COLORS[sp.stage] ?? '#999' }} onClick={onSel}>
      <Crest npc={npc} stage={sp.stage} />
      <span class="bd-card-main">
        <span class="bd-card-top">
          <span class="bd-card-name">{def.name}</span>
          {here && <span class="bd-here" title="In this area" />}
        </span>
        <span class="bd-card-stage">{BOND_STAGE_NAMES[sp.stage]}</span>
        <Bar tone="pink" value={sp.need ? sp.into / sp.need : 1} />
      </span>
    </button>
  );
}

function StageTrack({ stage, into, need }: { stage: number; into: number; need: number }) {
  const n = BOND_STAGE_NAMES.length;
  const frac = Math.min(1, (stage + (need ? into / need : 0)) / (n - 1));
  return (
    <div class="bd-track">
      <div class="bd-track-line">
        <i style={{ width: `${(frac * 100).toFixed(2)}%` }} />
      </div>
      {BOND_STAGE_NAMES.map((name, i) => (
        <div key={name} class={`bd-node${i <= stage ? ' on' : ''}${i === stage ? ' cur' : ''}`} style={{ '--c': STAGE_COLORS[i] ?? '#999', left: `${(i / (n - 1)) * 100}%` }}>
          <i />
          <span>{name}</span>
          <small>{BOND_STAGES[i]}</small>
        </div>
      ))}
    </div>
  );
}

function Detail({ game, npc, reaction, onReaction }: { game: Game; npc: string; reaction: Reaction | null; onReaction: (r: Reaction) => void }) {
  const [mode, setMode] = useState<Mode>('song');
  const prog = game.progression;
  const def = NPCS[npc];
  if (!prog || !def) return null;
  const c = prog.char;
  const day = game.account.day;
  const prof = profileFor(npc);
  const pts = c.bonds[npc] ?? 0;
  const sp = stageProgress(pts);
  const pres = npcPresence(game, npc);
  const maxed = sp.stage >= MAX_STAGE;
  const can = !!pres && !maxed;
  const used = dailyUsed(c, day);
  const heard = songsHeardToday(c, day, npc);
  const run = (r: BondResult, anim?: () => void) => {
    if (!r.ok) {
      game.hud.toast(r.reason, '#e5484d');
      return;
    }
    anim?.();
    onReaction({ npc, text: r.line, points: r.points, tone: r.tone, stageUp: r.stageUp ? r.stage : null });
    game.store.saveSoon(game.account);
    bump();
  };
  const gifts = Object.entries(c.inventory.stacks)
    .filter(([id, n]) => n > 0 && isGiftable(id))
    .map(([id, n]) => ({ id, n, pref: giftPreference(npc, id), pts: giftPoints(npc, id) }))
    .sort((a, b) => b.pts - a.pts);
  const blocked = maxed ? `${def.name} is already Devoted to you.` : !pres ? `Visit ${def.name} in ${prof.home} to spend time together.` : null;
  const left: Record<Mode, string> = {
    song: `${SONGS_PER_DAY - used.songs}/${SONGS_PER_DAY}`,
    emote: `${EMOTES_PER_DAY - used.emotes}/${EMOTES_PER_DAY}`,
    gift: `${GIFTS_PER_DAY - used.gifts}/${GIFTS_PER_DAY}`,
  };
  const react = reaction?.npc === npc ? reaction : null;
  return (
    <div class="bd-detail" style={{ '--stage': STAGE_COLORS[sp.stage] ?? '#999' }}>
      <div class="bd-d-head">
        <Crest npc={npc} stage={sp.stage} size={62} />
        <div class="bd-d-id">
          <div class="bd-d-role">[{def.role}]</div>
          <div class="bd-d-name">{def.name}</div>
          <div class={`bd-where${pres ? ' here' : ''}`}>{pres ? (pres.dist <= 12 ? 'Nearby' : `In this area · ${Math.round(pres.dist)} m away`) : `Lives in ${prof.home}`}</div>
        </div>
        <div class="bd-d-stage">
          <b>{BOND_STAGE_NAMES[sp.stage]}</b>
          <small>{sp.need ? `${sp.into} / ${sp.need} to ${BOND_STAGE_NAMES[sp.stage + 1] ?? ''}` : `${pts} bond`}</small>
        </div>
      </div>
      <StageTrack stage={sp.stage} into={sp.into} need={sp.need} />
      <div class="bd-modes" role="tablist">
        {(['song', 'emote', 'gift'] as const).map((m) => (
          <button key={m} class="tab" role="tab" aria-selected={mode === m} onClick={() => setMode(m)}>
            {m === 'song' ? 'Play a song' : m === 'emote' ? 'Emote' : 'Give a gift'}
            <span class="bd-left">{left[m]}</span>
          </button>
        ))}
      </div>
      <div class="bd-action">
        {blocked && <div class="bd-blocked">{blocked}</div>}
        {mode === 'song' && (
          <div class="bd-songs">
            {SONGS.map((s) => {
              const fav = prof.favoriteSong === s.id;
              const done = heard.includes(s.id);
              return (
                <button
                  key={s.id}
                  class={`bd-song${fav ? ' fav' : ''}`}
                  disabled={!can || done || used.songs >= SONGS_PER_DAY}
                  onClick={() => run(playSong(prog, day, npc, s.id), () => performSong(game, pres?.actor ?? null))}
                >
                  <span class="bd-note">♪</span>
                  <span class="bd-song-t">
                    <b>{s.name}</b>
                    <small>{done ? 'Heard today' : s.mood}</small>
                  </span>
                  {fav && <em>Favourite</em>}
                </button>
              );
            })}
          </div>
        )}
        {mode === 'emote' && (
          <div class="bd-emotes">
            {EMOTES.map((em) => {
              const liked = prof.likedEmote === em.id;
              const disliked = prof.dislikedEmote === em.id;
              return (
                <button
                  key={em.id}
                  class={`bd-emote${liked ? ' liked' : ''}${disliked ? ' disliked' : ''}`}
                  disabled={!can || used.emotes >= EMOTES_PER_DAY}
                  title={liked ? `${def.name} loves this` : disliked ? `${def.name} dislikes this` : em.name}
                  onClick={() => run(emoteFor(prog, day, npc, em.id), () => performPlayerEmote(game, em.id, { bond: false }))}
                >
                  <EmoteGlyph id={em.id} />
                  <span>{em.name}</span>
                  {(liked || disliked) && <i>{liked ? '♥' : '✕'}</i>}
                </button>
              );
            })}
          </div>
        )}
        {mode === 'gift' && (
          <div class="bd-gifts">
            {gifts.length === 0 && <div class="bd-empty">Nothing in your bag they would want. Gather materials or craft remedies.</div>}
            {gifts.map((g) => (
              <button key={g.id} class={`bd-gift ${g.pref}`} disabled={!can || used.gifts >= GIFTS_PER_DAY} title={`${itemName(g.id)} — +${g.pts} bond (${g.pref})`} onClick={() => run(giveGift(prog, day, npc, g.id))}>
                <ItemIcon id={g.id} size={40} count={g.n} />
                <span class="bd-gift-pts">+{g.pts}</span>
                {PREF_MARK[g.pref] && <i class="bd-pref">{PREF_MARK[g.pref]}</i>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div class={`bd-react ${react?.tone ?? 'idle'}`} key={react ? `${react.text}:${react.points}` : 'idle'}>
        {react ? (
          <>
            <span>{react.text}</span>
            <b>+{react.points}</b>
            {react.stageUp !== null && <em>Now {BOND_STAGE_NAMES[react.stageUp]}!</em>}
          </>
        ) : (
          <span class="muted">{prof.taste}</span>
        )}
      </div>
      <div class="bd-rewards">
        <div class="section-h">Stage rewards</div>
        {[1, 2, 3, 4].map((st) => {
          const r = stageRewards(npc, st);
          const got = st <= sp.stage;
          return (
            <div key={st} class={`bd-rw${got ? ' got' : ''}`}>
              <span class="bd-rw-stage" style={{ color: STAGE_COLORS[st] }}>
                {BOND_STAGE_NAMES[st]}
              </span>
              <span class="bd-rw-items">
                {r.items.map((it) => (
                  <ItemIcon key={it.id} id={it.id} size={24} count={it.count > 1 ? it.count : undefined} />
                ))}
                {r.card && <span class="bd-rw-tag card">Card · {CARDS[r.card]?.name ?? r.card}</span>}
                {r.title && <span class="bd-rw-tag title">Title · {r.title}</span>}
                {r.silver > 0 && <Silver n={r.silver} />}
              </span>
              <span class="bd-check">{got ? '✔' : ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function BondsPanel() {
  void ui.version.value;
  const arg = ui.panelArg.value;
  const npcs = bondNpcs();
  const [sel, setSel] = useState<string>(arg && npcs.includes(arg) ? arg : (npcs[0] ?? ''));
  const [reaction, setReaction] = useState<Reaction | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (arg && npcs.includes(arg)) setSel(arg);
  }, [arg]);
  // Distances change as the player walks around.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const game = bondsGame.current;
  const prog = game?.progression;
  if (!game || !prog)
    return (
      <Window title="Bonds" width={420}>
        <p class="muted">No character loaded.</p>
      </Window>
    );
  const used = dailyUsed(prog.char, game.account.day);
  // Nearby NPCs first, then by bond.
  const order = [...npcs].sort((a, b) => Number(!!npcPresence(game, b)) - Number(!!npcPresence(game, a)) || (prog.char.bonds[b] ?? 0) - (prog.char.bonds[a] ?? 0));
  return (
    <Window title="Bonds" width={900} className="bd-window">
      <div class="bd-header">
        <div class="bd-daily">
          <span>
            <b>♪</b> Songs <em>{SONGS_PER_DAY - used.songs}</em>/{SONGS_PER_DAY}
          </span>
          <span>
            <b>☺</b> Emotes <em>{EMOTES_PER_DAY - used.emotes}</em>/{EMOTES_PER_DAY}
          </span>
          <span>
            <b>✦</b> Gifts <em>{GIFTS_PER_DAY - used.gifts}</em>/{GIFTS_PER_DAY}
          </span>
        </div>
        <div class="bd-day">Day {game.account.day} · limits refresh each new day</div>
      </div>
      <div class="bd-body">
        <div class="bd-list">
          {order.map((n) => (
            <NpcCard key={n} game={game} npc={n} sel={n === sel} onSel={() => setSel(n)} />
          ))}
        </div>
        {sel && <Detail game={game} npc={sel} reaction={reaction} onReaction={setReaction} />}
      </div>
    </Window>
  );
}
